import { useEffect } from "react";
import { PaletteMode, useMediaQuery } from "@mui/material";
import { useSetAtom } from "jotai";
import { computeFiscalYears } from "../../common/fiscalPeriods";
import { getCurrentUserData } from "../../state/auth";
import { getUserPeriodFilter, userPeriodFilter } from "../../state/company";
import { getLocalPreferences, setLocalPreferences } from "../../state/preferences";
import AppLoadingView from "./AppLoadingView";

/**
 * SuspenseFallback is the GreenRouter UI app loading screen.
 * This component is used in the application index as an argument of a React Suspense component.
 *
 * @returns the main loading screen
 */
const SuspenseFallback = () => {
	const initUserPeriodFilter = useSetAtom(userPeriodFilter);

	// Local data must be loaded from the local storage because the application is still being loaded and atoms may not return data.
	const userData = getCurrentUserData();
	const filter = getUserPeriodFilter();

	// Load user local preferences (browser language is already stored by i18n initialization).
	const localPreferences = getLocalPreferences();

	// Detect OS theme mode.
	const userPrefersDarkMode = useMediaQuery("(prefers-color-scheme: dark)");
	const systemTheme: PaletteMode = userPrefersDarkMode ? "dark" : "light";

	// Update local storage.
	// Keep the other stored preferences (e.g. navigation preferences) untouched.
	setLocalPreferences({ ...localPreferences, systemTheme });

	// Load fiscal periods if the user has an open session.
	useEffect(() => {
		if (userData) {
			const fiscalPeriodParts = userData.company.fiscalPeriod.split("/");
			const fiscalYears = computeFiscalYears(fiscalPeriodParts[0], fiscalPeriodParts[1]);
			initUserPeriodFilter(
				filter ?? {
					start: fiscalYears[0].start,
					end: fiscalYears[0].end,
					selectedPeriodId: 0,
					type: "fiscal",
					fiscalYears,
				}
			);
		}
	}, [userData, filter, initUserPeriodFilter]);

	return <AppLoadingView />;
};

export default SuspenseFallback;
